import styled from 'styled-components';

import rem from 'polished/lib/helpers/rem';
import toColorString from 'polished/lib/color/toColorString';

import rgbaToHex from '@decisiv/ui-components/lib/utils/rgbaToHex';

import color from '@decisiv/design-tokens/lib/color';
import spacing from '@decisiv/design-tokens/lib/spacing';
import typography from '@decisiv/design-tokens/lib/typography';

// Real styled components rather than the `css` prop: portal's babel config has
// no babel-plugin-styled-components, so a `css={...}` is dropped without
// warning.
//
// The table is hand-rolled because KeyDS `Table` cannot express the design's
// two-tier header — it renders one <tr> with one <th> per column and offers no
// colSpan or rowSpan. The values here are copied from Table/Container.js so it
// still reads as the users list's table, and will not track a restyle of it.

const HAIRLINE = rgbaToHex(color.opacity.charcoal15, color.base.halfMoon);

// The header fill and the zebra KeyDS Table draws for kind="primary" — which is
// its default (Table/index.js), so it is what the users list renders. The values
// below had been copied from Container.js's base block only, which is why this
// table arrived with a white header row and no striping.
const HEADER_FILL = toColorString(color.base.halfMoon);
const STRIPE = rgbaToHex(color.opacity.fullMoon50, color.base.snowWhite);

// Carries the PERMISSIONS | DISCOVERABILITY split down the table. The band cells
// meet on a shared border, but below them nothing marks the boundary unless the
// first cell of the second band draws it.
const bandDivider = `
  border-left: 1px solid ${HAIRLINE};
`;

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${rem(spacing.base)};
  padding: ${rem(spacing.base * 1.5)} 0;
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${rem(spacing.base)};
`;

export const SectionHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(spacing.base / 4)};
`;

export const ScopeRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(spacing.base * 2)};
`;

// overflow-x rather than a wrapped layout: the matrix is wider than a narrow
// viewport, and the account name has to stay readable rather than be squeezed.
export const MatrixScroll = styled.div`
  width: 100%;
  overflow-x: auto;
`;

export const MatrixTable = styled.table`
  border-collapse: collapse;
  width: 100%;
  text-align: left;
  background-color: ${toColorString(color.base.snowWhite)};
  font-size: ${rem(typography.size.alias.default)};
`;

const headerCell = `
  font-size: ${rem(typography.size.alias.paragraph2)};
  font-weight: ${typography.weight.alias.semibold};
  line-height: ${typography.height.alias.tall};
  padding: ${rem(spacing.base * 1.5)} ${rem(spacing.base)};
  text-transform: uppercase;
`;

// The band over PERMISSIONS and DISCOVERABILITY. Boxed and centred, which is what
// makes the two read as groups rather than headings.
//
// The `:first-child` exemption is the empty corner above the account column:
// MatrixHead renders it as this same component so the corner needs no styled
// component of its own, and the reset strips the fill and the box the design
// starts only where PERMISSIONS does. MatrixHead renders it `as="td"` — there is
// no heading there, and an empty th is announced as one.
export const BandCell = styled.th`
  ${headerCell}
  text-align: center;
  vertical-align: bottom;
  border: 1px solid ${HAIRLINE};
  background-color: ${toColorString(color.opacity.fullMoon50)};

  &:first-child {
    border: none;
    background-color: transparent;
  }
`;

export const ColumnCell = styled.th`
  ${headerCell}
  vertical-align: bottom;
  white-space: nowrap;
  background-color: ${HEADER_FILL};
  box-shadow: inset 0 -2px ${HAIRLINE};

  &:first-child {
    padding-left: ${rem(spacing.base * 2)};
  }

  &:last-child {
    padding-right: ${rem(spacing.base * 2)};
  }
`;

// Centred like the controls beneath them; the account column stays left.
export const ControlColumnCell = styled(ColumnCell)`
  text-align: center;
  ${({ $bandStart }) => ($bandStart ? bandDivider : '')}
`;

export const ColumnLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${rem(spacing.base / 4)};
`;

// The team band. A <th> rather than a styled <td> so a screen reader is told the
// rows belong to it; PermissionsMatrix gives it scope="rowgroup" and its own
// <tbody>, which is what makes that association real.
export const GroupCell = styled.th`
  ${headerCell}
  background-color: ${toColorString(color.interaction.arcticOcean40)};
  padding-left: ${rem(spacing.base * 2)};
  border-top: 1px solid ${HAIRLINE};
  border-bottom: 1px solid ${HAIRLINE};
`;

// $stripe rather than :nth-of-type. Each team is its own tbody whose first child
// is the GroupCell's row, and PermissionsMatrix inserts a further <tr> under any
// row carrying a server refusal — so counting rows in CSS puts the stripe on the
// wrong ones and inverts it below every error. KeyDS has the same problem and
// solves it the same way: nth-of-type for flat tables, explicit
// group-data-row-even/odd classes once the rows are grouped.
export const DataRow = styled.tr`
  background-color: ${({ $stripe }) =>
    $stripe ? STRIPE : toColorString(color.base.snowWhite)};
  border-top: 1px solid ${HAIRLINE};

  &:last-of-type {
    border-bottom: 1px solid ${HAIRLINE};
  }
`;

export const AccountCell = styled.th`
  font-weight: ${typography.weight.alias.regular};
  padding: ${rem(spacing.base)} ${rem(spacing.base)} ${rem(spacing.base)}
    ${rem(spacing.base * 2)};
  white-space: nowrap;
`;

export const ControlCell = styled.td`
  padding: ${rem(spacing.base)};
  text-align: center;
  vertical-align: middle;
  ${({ $bandStart }) => ($bandStart ? bandDivider : '')}

  &:last-child {
    padding-right: ${rem(spacing.base * 2)};
  }
`;

// Centres the control inside its cell without giving the checkbox a width.
export const ControlSlot = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

// The focusable wrapper a Tooltip attaches to when the control it explains is
// disabled. KeyDS Tooltip binds native hover and focus events to its target, and
// a disabled input fires none of them, so a tooltip pointed straight at one
// never shows. This wrapper is neither disabled nor unfocusable.
export const LockedControl = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${rem(2)};

  &:focus-visible {
    outline: 1px solid ${toColorString(color.interaction.pacificOcean)};
    outline-offset: ${rem(2)};
  }
`;

// Where every tier DropdownList portals its panel, and the only place their
// options can be styled from: DropdownList builds the panel itself and forwards
// no `className`, so `styled(DropdownList)` reaches nothing. Pointing its
// `containerSelector` at this div makes it the panel's ancestor.
//
// Mounted once by PermissionsMatrix, outside MatrixScroll — that wrapper is
// `overflow-x: auto` and would clip a panel portalled into a cell inside it.
//
// The rules swap OptionsList's own fills, which are drawn for a plain text
// label: solid pacificOcean behind a pastel role tag reads as a rendering fault
// rather than as selection. Only the fill goes — `aria-selected` still says
// which tier is held, and the row still highlights.
export const TIER_OPTIONS_PORTAL_ID = 'tier-options-portal';

export const TierOptionsPortal = styled.div`
  li[role='option'] {
    background: none;
  }

  li[role='option'] > .label {
    background: none;
    color: ${toColorString(color.base.charcoal)};
    padding: ${rem(spacing.base / 2)} ${rem(spacing.base)};
  }

  /* color as well as background: the fill being dropped here came with white
     text, which has nothing to sit on once the fill is gone. */
  li[role='option'][aria-selected='true'] > .label,
  li[role='option']:hover:not([aria-disabled='true']) > .label {
    background: ${toColorString(color.interaction.arcticOcean40)};
    color: ${toColorString(color.base.charcoal)};
  }
`;

export const InfoTipButton = styled.button`
  display: inline-flex;
  align-items: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  line-height: 0;
`;

// A refusal that belongs to a row rather than to the form as a whole.
export const RowError = styled.div`
  color: ${toColorString(color.status.danger.medium)};
  font-size: ${rem(typography.size.alias.small)};
  padding: 0 ${rem(spacing.base)} ${rem(spacing.base)} ${rem(spacing.base * 2)};
`;

export const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(spacing.base / 2)};
  padding-bottom: ${rem(spacing.base * 2)};
`;

export const AccountsSectionHeader = styled.div`
  margin: ${rem(spacing.base)} 0;
`;

export const AccountsList = styled.div`
  display: flex;
  flex-direction: column;
`;

export const AccountRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(spacing.base / 4)};
  padding: ${rem(spacing.base)} 0;
  border-bottom: 1px solid ${toColorString(color.base.halfMoon)};

  &:first-child {
    padding-top: 0;
  }
`;

export const AccountRowHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(spacing.base)};
`;

export const AccountRowTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(spacing.base / 2)};
  flex-wrap: wrap;
`;

export const AccountRowActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(spacing.base / 2)};
  flex-shrink: 0;
`;

const IDENTITY_BG = rgbaToHex(color.opacity.fullMoon50, color.base.snowWhite);

export const AddToAccountIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(spacing.base)};
  padding: ${rem(spacing.base)};
  margin: ${rem(spacing.base * 1.5)} 0;
  background-color: ${IDENTITY_BG};
`;
